import { useState, useRef, useCallback } from 'react';
import ReactCrop, { type Crop, centerCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { Camera, Upload, X, Check } from 'lucide-react';
import { ERROR_TEXT, LABEL_TEXT } from './ui/tokens';

const ACCEPTED_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif']);
const MAX_BYTES = 10 * 1024 * 1024;

interface MiembroFotoUploadProps {
  readonly onFotoReady: (dataUri: string) => void;
  readonly onClear?: () => void;
  readonly error?: string;
  readonly required?: boolean;
  readonly initialUrl?: string;
  readonly disabled?: boolean;
}

export const MiembroFotoUpload: React.FC<MiembroFotoUploadProps> = ({
  onFotoReady,
  onClear,
  error,
  required = false,
  initialUrl,
  disabled = false,
}) => {
  const [imgSrc, setImgSrc] = useState('');
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<Crop>();
  const [processing, setProcessing] = useState(false);
  const [localError, setLocalError] = useState('');
  const [preview, setPreview] = useState(initialUrl ?? '');
  const imgRef = useRef<HTMLImageElement>(null);
  const uploadInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  const onImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const { naturalWidth: w, naturalHeight: h } = e.currentTarget;
    const c = centerCrop(makeAspectCrop({ unit: '%', width: 90 }, 1, w, h), w, h);
    setCrop(c);
    setCompletedCrop(c);
  };

  const processFile = useCallback(async (file: File) => {
    setLocalError('');
    const isHeic = /\.(heic|heif)$/i.test(file.name) || file.type === 'image/heic' || file.type === 'image/heif';
    if (!ACCEPTED_TYPES.has(file.type) && !isHeic) {
      setLocalError('Formato no admitido. Usa JPEG, PNG, WebP o HEIC.');
      return;
    }
    if (file.size > MAX_BYTES) {
      setLocalError('La foto supera los 10 MB. Elige una más pequeña.');
      return;
    }
    setProcessing(true);
    try {
      let blob: Blob = file;
      if (isHeic) {
        const heic2any = (await import('heic2any')).default;
        blob = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.9 }) as Blob;
      }
      const reader = new FileReader();
      reader.onload = () => { if (typeof reader.result === 'string') setImgSrc(reader.result); };
      reader.readAsDataURL(blob);
    } catch {
      setLocalError('No pudimos procesar esta imagen. Intenta con una foto en formato JPEG o PNG.');
    } finally {
      setProcessing(false);
    }
  }, []);

  const handleConfirm = useCallback(() => {
    if (!completedCrop || !imgRef.current) return;
    const img = imgRef.current;
    const scaleX = img.naturalWidth / img.width;
    const scaleY = img.naturalHeight / img.height;
    const srcW = completedCrop.width * scaleX;
    const srcH = completedCrop.height * scaleY;
    const outW = Math.min(Math.round(srcW), 400);
    const outH = Math.min(Math.round(srcH), 400);

    const canvas = document.createElement('canvas');
    canvas.width = outW;
    canvas.height = outH;
    const ctx = canvas.getContext('2d')!;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(img, completedCrop.x * scaleX, completedCrop.y * scaleY, srcW, srcH, 0, 0, outW, outH);
    canvas.toBlob(blob => {
      if (!blob) return;
      const reader = new FileReader();
      reader.onloadend = () => {
        const uri = reader.result as string;
        setPreview(uri);
        setImgSrc('');
        onFotoReady(uri);
      };
      reader.readAsDataURL(blob);
    }, 'image/webp', 0.82);
  }, [completedCrop, onFotoReady]);

  const handleClear = () => {
    setPreview('');
    setImgSrc('');
    onClear?.();
  };

  return (
    <div className="space-y-3">
      <p className={LABEL_TEXT}>
        Fotografía{required && <span className="text-red-500 ml-1">*</span>}
      </p>
      {!imgSrc && !preview && (
        <>
          <input
            ref={uploadInputRef} type="file" accept="image/*"
            aria-label="Subir una foto desde el dispositivo"
            disabled={disabled}
            className="hidden"
            onChange={e => { const f = e.target.files?.[0]; if (f) { processFile(f); } e.target.value = ''; }}
          />
          <input
            ref={cameraInputRef} type="file" accept="image/*" capture="user"
            aria-label="Tomar una foto con la cámara"
            disabled={disabled}
            className="hidden"
            onChange={e => { const f = e.target.files?.[0]; if (f) { processFile(f); } e.target.value = ''; }}
          />
          {processing ? (
            <button type="button" disabled
              className="flex items-center gap-2 px-4 py-2 border border-slate-200 rounded-xl text-sm text-slate-600 opacity-50">
              <Camera className="w-4 h-4" /> Procesando...
            </button>
          ) : (
            <div className="flex flex-wrap gap-2">
              <button type="button" disabled={disabled} onClick={() => uploadInputRef.current?.click()}
                className="flex items-center gap-2 px-4 py-2 border border-slate-200 rounded-xl text-sm text-slate-600 hover:border-slate-300 disabled:opacity-50">
                <Upload className="w-4 h-4" /> Subir foto
              </button>
              <button type="button" disabled={disabled} onClick={() => cameraInputRef.current?.click()}
                className="flex items-center gap-2 px-4 py-2 border border-slate-200 rounded-xl text-sm text-slate-600 hover:border-slate-300 disabled:opacity-50">
                <Camera className="w-4 h-4" /> Tomar foto
              </button>
            </div>
          )}
        </>
      )}

      {imgSrc && (
        <div className="space-y-3">
          <p className="text-xs text-slate-500">Ajusta el recuadro y confirma. Para desplazar la página, usa los bordes laterales.</p>
          <div className="flex gap-2">
            <button type="button" disabled={disabled} onClick={handleConfirm}
              className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-medium disabled:opacity-50">
              <Check className="w-4 h-4" /> Confirmar recorte
            </button>
            <button type="button" disabled={disabled} onClick={() => setImgSrc('')}
              className="flex items-center gap-1.5 px-3 py-2 border border-slate-200 rounded-xl text-sm text-slate-600 disabled:opacity-50">
              <X className="w-4 h-4" /> Cancelar
            </button>
          </div>
          <ReactCrop crop={crop} onChange={setCrop} onComplete={setCompletedCrop} aspect={1} minWidth={80}>
            <img ref={imgRef} src={imgSrc} alt="Recortar" onLoad={onImageLoad} className="max-h-[50vh] w-auto rounded-lg" />
          </ReactCrop>
        </div>
      )}

      {preview && (
        <div className="flex items-center gap-3">
          <img src={preview} alt="Foto" className="w-20 h-20 rounded-full object-cover border border-slate-200" />
          <button type="button" disabled={disabled} onClick={handleClear}
            className="text-sm text-slate-500 hover:text-slate-700 disabled:opacity-50">
            Cambiar foto
          </button>
        </div>
      )}

      {(localError || error) && <p className={ERROR_TEXT}>{localError || error}</p>}
    </div>
  );
};
